import React, { useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Line,
  ReferenceLine,
} from "recharts";
import { getDetailedClimateColor } from "../utils/climateClassification";
import koppenDescriptions from "../data/koppen-descriptions.json";
import {
  OPENROUTER_API_KEY,
  API_BASE_URL,
  isConfigValid,
} from "../utils/config";

const TemperatureAnalysis = ({ climateInfo, temperatureData }) => {
  const [aiInterpretation, setAiInterpretation] = useState(null);
  const [loadingAiInterpretation, setLoadingAiInterpretation] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [designSuggestions, setDesignSuggestions] = useState(null);
  const [loadingDesignSuggestions, setLoadingDesignSuggestions] =
    useState(false);
  const [designError, setDesignError] = useState(null);

  // Helper function to get detailed description
  const getDetailedDescription = () => {
    if (!climateInfo) return "";

    const { mainClass, subClass } = climateInfo;

    // If we don't have a valid classification, return a default message
    if (mainClass === "Unknown" || !koppenDescriptions[mainClass]) {
      return "This location's climate does not fit standard Köppen-Geiger classifications based on the available data.";
    }

    let description = koppenDescriptions[mainClass].description;

    // Add subtype description if available
    if (subClass && koppenDescriptions[mainClass].subtypes) {
      // Extract the precipitation pattern (first letter of subClass)
      const precipPattern = subClass.charAt(0);

      if (koppenDescriptions[mainClass].subtypes[precipPattern]) {
        description +=
          " " +
          koppenDescriptions[mainClass].subtypes[precipPattern].description;

        // Add temperature modifier description if available (for C and D climates)
        if (
          (mainClass === "C" || mainClass === "D") &&
          subClass.length > 1 &&
          koppenDescriptions[mainClass].subtypes[precipPattern]
            .temperature_modifiers
        ) {
          const tempModifier = subClass.charAt(1);
          if (
            koppenDescriptions[mainClass].subtypes[precipPattern]
              .temperature_modifiers[tempModifier]
          ) {
            description +=
              " This region is " +
              koppenDescriptions[mainClass].subtypes[precipPattern]
                .temperature_modifiers[tempModifier];
          }
        }
      }
    }

    return description;
  };

  if (!climateInfo || !temperatureData) {
    return null;
  }

  // Calculate thermal performance metrics for use throughout component
  const baseTemp = 18; // Base temperature for HDD/CDD calculation

  const hdd = temperatureData.data
    ? temperatureData.data.reduce((sum, d) => {
        const dailyMean = d.mean;
        return sum + (dailyMean < baseTemp ? baseTemp - dailyMean : 0);
      }, 0)
    : 0;

  const cdd = temperatureData.data
    ? temperatureData.data.reduce((sum, d) => {
        const dailyMean = d.mean;
        return sum + (dailyMean > baseTemp ? dailyMean - baseTemp : 0);
      }, 0)
    : 0;

  return (
    <div
      id="temperature-analysis-section"
      className="min-h-screen bg-gray-100 px-14 py-8 pt-28"
    >
      {/* Temperature Heading */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 tracking-wide">
          Temperature
        </h1>
      </div>

      {/* Three Cards Layout - Top Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* MACRO Card */}
        <div className="bg-gray-100 border border-gray-500 p-8 rounded-[2rem]">
          <div className="border-b border-gray-300 pb-2 mb-4">
            <h3 className="text-lg font-bold text-gray-800 tracking-wide">
              MACRO
            </h3>
          </div>

          {/* Top section - temperatures only */}
          <div className="flex justify-between items-start mb-8">
            {/* Left side - Max and Min temperatures (smaller) */}
            <div className="flex-1">
              <div className="mb-4">
                <div className="text-sm text-gray-500 font-bold mb-1">
                  Maximum Temperature
                </div>
                <div className="text-2xl font-black text-gray-900">
                  {temperatureData.data
                    ? Math.max(
                        ...temperatureData.data.map((d) => d.max)
                      ).toFixed(1)
                    : "--"}
                  °C
                </div>
              </div>

              <div>
                <div className="text-sm text-gray-500 font-bold mb-1">
                  Minimum Temperature
                </div>
                <div className="text-2xl font-black text-gray-900">
                  {temperatureData.data
                    ? Math.min(
                        ...temperatureData.data.map((d) => d.min)
                      ).toFixed(1)
                    : "--"}
                  °C
                </div>
              </div>
            </div>

            {/* Right side - Annual average temperature (large, aligned top) */}
            <div className="flex-1 text-right">
              <div className="text-5xl font-black text-black mb-2">
                {temperatureData.data
                  ? (
                      temperatureData.data.reduce((sum, d) => sum + d.mean, 0) /
                      temperatureData.data.length
                    ).toFixed(1)
                  : "--"}
                °C
              </div>
              <div className="text-sm text-gray-500 font-bold">
                Annual Avg. Temperature
              </div>
            </div>
          </div>

          {/* Bottom section - Classification information */}
          <div>
            {/* Large Köppen classification code */}
            <div
              className="text-6xl font-black leading-none mb-4"
              style={{
                color: getDetailedClimateColor(
                  climateInfo.mainClass,
                  climateInfo.subClass
                ),
              }}
            >
              {climateInfo.mainClass}
              {climateInfo.subClass}
            </div>

            {/* Classification label */}
            <div className="text-sm text-gray-500 font-bold mb-4">
              Köppen-Geiger Climate Classification
            </div>

            {/* Climate name */}
            <div className="font-bold text-gray-800 mb-4 text-lg">
              {climateInfo.fullName}
            </div>

            {/* Description */}
            <div className="text-sm text-gray-500 leading-relaxed font-bold">
              <div>
                Continental climates feature large seasonal temperature
                differences with warm to hot summers and cold winters.
                {climateInfo.subClass &&
                  climateInfo.subClass.includes("f") &&
                  " No pronounced dry season with consistent precipitation year-round."}
                {climateInfo.subClass &&
                  climateInfo.subClass.includes("a") &&
                  " Hot summers with warmest month above 22°C."}
              </div>
            </div>
          </div>
        </div>

        {/* MICRO Card */}
        <div className="bg-gray-100 border border-gray-500 p-8 rounded-[2rem] flex flex-col h-full">
          <div className="border-b border-gray-300 pb-2 mb-4">
            <h3 className="text-lg font-bold text-gray-800 tracking-wide">
              MICRO
            </h3>
          </div>

          {/* Temperature Range at top-left */}
          <div className="text-left mb-8">
            <div className="text-5xl font-black text-black mb-2">
              {temperatureData.data
                ? (
                    Math.max(...temperatureData.data.map((d) => d.max)) -
                    Math.min(...temperatureData.data.map((d) => d.min))
                  ).toFixed(1)
                : "--"}
              °C
            </div>
            <div className="text-sm text-gray-500 font-bold">
              Temperature Range
            </div>
          </div>

          {/* Seasonal Extremes */}
          <div className="mb-8 flex-grow">
            <h4 className="font-bold text-gray-500 mb-4 text-lg">
              Seasonal Extremes
            </h4>

            <div className="grid grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-4 text-sm">
                <div>
                  <div className="font-bold text-gray-800 mb-1">Winter:</div>
                  <div className="font-bold text-gray-500">
                    Max:{" "}
                    {temperatureData.data
                      ? (() => {
                          const winterData = temperatureData.data.filter(
                            (item, index) => {
                              const month = item.month
                                ? item.month
                                : Math.floor(
                                    index / (temperatureData.data.length / 12)
                                  ) + 1;
                              return month === 12 || month === 1 || month === 2;
                            }
                          );
                          return winterData.length > 0
                            ? Math.max(...winterData.map((d) => d.max)).toFixed(
                                1
                              )
                            : "--";
                        })()
                      : "--"}
                    °C | Min:{" "}
                    {temperatureData.data
                      ? (() => {
                          const winterData = temperatureData.data.filter(
                            (item, index) => {
                              const month = item.month
                                ? item.month
                                : Math.floor(
                                    index / (temperatureData.data.length / 12)
                                  ) + 1;
                              return month === 12 || month === 1 || month === 2;
                            }
                          );
                          return winterData.length > 0
                            ? Math.min(...winterData.map((d) => d.min)).toFixed(
                                1
                              )
                            : "--";
                        })()
                      : "--"}
                    °C
                  </div>
                </div>

                <div>
                  <div className="font-bold text-gray-800 mb-1">Spring:</div>
                  <div className="font-bold text-gray-500">
                    Max:{" "}
                    {temperatureData.data
                      ? (() => {
                          const springData = temperatureData.data.filter(
                            (item, index) => {
                              const month = item.month
                                ? item.month
                                : Math.floor(
                                    index / (temperatureData.data.length / 12)
                                  ) + 1;
                              return month === 3 || month === 4 || month === 5;
                            }
                          );
                          return springData.length > 0
                            ? Math.max(...springData.map((d) => d.max)).toFixed(
                                1
                              )
                            : "--";
                        })()
                      : "--"}
                    °C | Min:{" "}
                    {temperatureData.data
                      ? (() => {
                          const springData = temperatureData.data.filter(
                            (item, index) => {
                              const month = item.month
                                ? item.month
                                : Math.floor(
                                    index / (temperatureData.data.length / 12)
                                  ) + 1;
                              return month === 3 || month === 4 || month === 5;
                            }
                          );
                          return springData.length > 0
                            ? Math.min(...springData.map((d) => d.min)).toFixed(
                                1
                              )
                            : "--";
                        })()
                      : "--"}
                    °C
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4 text-sm">
                <div>
                  <div className="font-bold text-gray-800 mb-1">Summer:</div>
                  <div className="font-bold text-gray-500">
                    Max:{" "}
                    {temperatureData.data
                      ? (() => {
                          const summerData = temperatureData.data.filter(
                            (item, index) => {
                              const month = item.month
                                ? item.month
                                : Math.floor(
                                    index / (temperatureData.data.length / 12)
                                  ) + 1;
                              return month === 6 || month === 7 || month === 8;
                            }
                          );
                          return summerData.length > 0
                            ? Math.max(...summerData.map((d) => d.max)).toFixed(
                                1
                              )
                            : "--";
                        })()
                      : "--"}
                    °C | Min:{" "}
                    {temperatureData.data
                      ? (() => {
                          const summerData = temperatureData.data.filter(
                            (item, index) => {
                              const month = item.month
                                ? item.month
                                : Math.floor(
                                    index / (temperatureData.data.length / 12)
                                  ) + 1;
                              return month === 6 || month === 7 || month === 8;
                            }
                          );
                          return summerData.length > 0
                            ? Math.min(...summerData.map((d) => d.min)).toFixed(
                                1
                              )
                            : "--";
                        })()
                      : "--"}
                    °C
                  </div>
                </div>

                <div>
                  <div className="font-bold text-gray-800 mb-1">Fall:</div>
                  <div className="font-bold text-gray-500">
                    Max:{" "}
                    {temperatureData.data
                      ? (() => {
                          const fallData = temperatureData.data.filter(
                            (item, index) => {
                              const month = item.month
                                ? item.month
                                : Math.floor(
                                    index / (temperatureData.data.length / 12)
                                  ) + 1;
                              return (
                                month === 9 || month === 10 || month === 11
                              );
                            }
                          );
                          return fallData.length > 0
                            ? Math.max(...fallData.map((d) => d.max)).toFixed(1)
                            : "--";
                        })()
                      : "--"}
                    °C | Min:{" "}
                    {temperatureData.data
                      ? (() => {
                          const fallData = temperatureData.data.filter(
                            (item, index) => {
                              const month = item.month
                                ? item.month
                                : Math.floor(
                                    index / (temperatureData.data.length / 12)
                                  ) + 1;
                              return (
                                month === 9 || month === 10 || month === 11
                              );
                            }
                          );
                          return fallData.length > 0
                            ? Math.min(...fallData.map((d) => d.min)).toFixed(1)
                            : "--";
                        })()
                      : "--"}
                    °C
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Temperature Extremes - Large display */}
          <div className="flex justify-between items-center mt-auto">
            <div className="text-left">
              <div className="text-4xl font-black text-black">
                {temperatureData.data
                  ? Math.min(...temperatureData.data.map((d) => d.min)).toFixed(
                      1
                    )
                  : "--"}
                °C
              </div>
              <div className="text-sm text-gray-500 font-bold">
                minimum temperature
              </div>
            </div>
            <div className="text-right">
              <div className="text-4xl font-black text-black">
                {temperatureData.data
                  ? Math.max(...temperatureData.data.map((d) => d.max)).toFixed(
                      1
                    )
                  : "--"}
                °C
              </div>
              <div className="text-sm text-gray-500 font-bold">
                maximum temperature
              </div>
            </div>
          </div>
        </div>

        {/* DESIGN Card */}
        <div className="bg-gray-100 border border-gray-500 p-8 rounded-[2rem] flex flex-col h-full">
          <div className="border-b border-gray-300 pb-2 mb-4">
            <h3 className="text-lg font-bold text-gray-800 tracking-wide">
              DESIGN
            </h3>
          </div>

          <div className="flex space-x-4 mb-4">
            <div className="w-20 h-14 bg-gray-800 rounded flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-white rounded-sm"></div>
            </div>
            <div className="w-20 h-14 bg-gray-800 rounded flex items-center justify-center">
              <div className="w-8 h-8 bg-white transform rotate-45"></div>
            </div>
          </div>

          <div className="mb-4 flex-grow">
            <h4 className="font-bold text-gray-800 mb-2">
              Climate Interpretation & Design Implications
            </h4>

            {/* Conditional content: Show loading, AI suggestions, error, or static text */}
            {loadingDesignSuggestions ? (
              <div className="text-sm text-gray-700 space-y-2">
                <style jsx>{`
                  @keyframes designWave1 {
                    0%,
                    100% {
                      background-color: #d1d5db;
                    }
                    50% {
                      background-color: #9ca3af;
                    }
                  }
                  @keyframes designWave2 {
                    0%,
                    100% {
                      background-color: #9ca3af;
                    }
                    50% {
                      background-color: #d1d5db;
                    }
                  }
                  @keyframes designWave3 {
                    0%,
                    100% {
                      background-color: #e5e7eb;
                    }
                    50% {
                      background-color: #9ca3af;
                    }
                  }
                  .animate-design-wave-1 {
                    animation: designWave1 2s ease-in-out infinite;
                  }
                  .animate-design-wave-2 {
                    animation: designWave2 2s ease-in-out infinite 0.3s;
                  }
                  .animate-design-wave-3 {
                    animation: designWave3 2s ease-in-out infinite 0.6s;
                  }
                `}</style>
                <div className="h-3 rounded animate-design-wave-1"></div>
                <div className="h-3 rounded animate-design-wave-2 w-4/5"></div>
                <div className="h-3 rounded animate-design-wave-3 w-3/5"></div>
                <div className="h-3 rounded animate-design-wave-1 w-5/6"></div>
                <div className="h-3 rounded animate-design-wave-2 w-2/3"></div>
                <div className="h-3 rounded animate-design-wave-3"></div>
              </div>
            ) : designSuggestions ? (
              <div className="text-sm text-gray-500 leading-relaxed font-bold whitespace-pre-wrap">
                {designSuggestions}
              </div>
            ) : designError ? (
              <div className="text-red-600 text-sm font-bold">
                Error: {designError}
              </div>
            ) : (
              <div className="text-sm text-gray-700 space-y-2">
                <p>
                  This location has{" "}
                  {parseFloat(climateInfo.annualTemp) < 10
                    ? "significant heating requirements and moderate cooling needs"
                    : parseFloat(climateInfo.annualTemp) > 25
                    ? "significant cooling requirements and moderate heating needs"
                    : "moderate heating and cooling needs"}
                  {temperatureData.data
                    ? ` with an annual average of ${(
                        temperatureData.data.reduce(
                          (sum, d) => sum + d.mean,
                          0
                        ) / temperatureData.data.length
                      ).toFixed(1)}°C`
                    : ""}
                  {temperatureData.data
                    ? ` (${hdd.toFixed(0)} HDD, ${cdd.toFixed(0)} CDD)`
                    : ""}
                  .
                </p>
                <p>
                  The{" "}
                  {temperatureData.data
                    ? `${(
                        Math.max(...temperatureData.data.map((d) => d.max)) -
                        Math.min(...temperatureData.data.map((d) => d.min))
                      ).toFixed(1)}°C annual temperature range`
                    : "temperature range"}{" "}
                  {temperatureData.data &&
                  Math.max(...temperatureData.data.map((d) => d.max)) -
                    Math.min(...temperatureData.data.map((d) => d.min)) <
                    30
                    ? "indicates relatively moderate temperature variation"
                    : "indicates significant temperature variation"}{" "}
                  throughout the year
                  {temperatureData.data
                    ? `, with ${(
                        (temperatureData.data.filter(
                          (d) => d.mean >= 18 && d.mean <= 26
                        ).length /
                          temperatureData.data.length) *
                        100
                      ).toFixed(1)}% of days in the comfortable range (18-26°C)`
                    : ""}
                  .
                </p>
              </div>
            )}

            {/* Design Suggestion Text and AI Button */}
            <div className="flex items-center gap-3 mt-6">
              <span className="text-black text-sm font-bold">
                Design Suggestion
              </span>
              <button
                className="bg-gray-600 hover:bg-[#206ab2] text-white px-1.5 py-1 rounded text-xs font-bold transition-colors"
                onClick={async () => {
                  if (!OPENROUTER_API_KEY) {
                    setDesignError("OpenRouter API key not configured");
                    return;
                  }

                  // Clear previous results before starting new analysis
                  setDesignSuggestions(null);
                  setDesignError(null);
                  setLoadingDesignSuggestions(true);

                  try {
                    // Calculate thermal performance metrics
                    const baseTemp = 18; // Base temperature for HDD/CDD calculation

                    // Calculate Heating Degree Days (HDD) and Cooling Degree Days (CDD)
                    const hdd = temperatureData.data
                      ? temperatureData.data.reduce((sum, d) => {
                          const dailyMean = d.mean;
                          return (
                            sum +
                            (dailyMean < baseTemp ? baseTemp - dailyMean : 0)
                          );
                        }, 0)
                      : 0;

                    const cdd = temperatureData.data
                      ? temperatureData.data.reduce((sum, d) => {
                          const dailyMean = d.mean;
                          return (
                            sum +
                            (dailyMean > baseTemp ? dailyMean - baseTemp : 0)
                          );
                        }, 0)
                      : 0;

                    // Calculate Diurnal Temperature Range (DTR) - average daily temperature swing
                    const dailyRanges = temperatureData.data
                      ? temperatureData.data.map((d) => d.max - d.min)
                      : [];
                    const avgDTR =
                      dailyRanges.length > 0
                        ? dailyRanges.reduce((sum, range) => sum + range, 0) /
                          dailyRanges.length
                        : 0;

                    // Calculate extreme temperatures
                    const annualMax = temperatureData.data
                      ? Math.max(...temperatureData.data.map((d) => d.max))
                      : 0;
                    const annualMin = temperatureData.data
                      ? Math.min(...temperatureData.data.map((d) => d.min))
                      : 0;

                    // Calculate annual average
                    const annualAvg = temperatureData.data
                      ? temperatureData.data.reduce(
                          (sum, d) => sum + d.mean,
                          0
                        ) / temperatureData.data.length
                      : 0;

                    const response = await fetch(
                      "https://openrouter.ai/api/v1/chat/completions",
                      {
                        method: "POST",
                        headers: {
                          Authorization: `Bearer ${OPENROUTER_API_KEY}`,
                          "HTTP-Referer": window.location.origin,
                          "X-Title": "ClimaPro.AI",
                          "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                          model: "anthropic/claude-3.7-sonnet",
                          route: "fallback",
                          messages: [
                            {
                              role: "system",
                              content:
                                "You are an expert building design consultant specializing in climate-responsive architecture for specific locations. Analyze the exact climate classification and thermal metrics provided to give location-specific building design recommendations. Structure your response as exactly 3 paragraphs: 1) Thermal envelope strategy with reasoning based on HDD/CDD values, 2) Construction approach justified by temperature extremes and DTR, 3) Passive design priorities explained using specific thermal data. Include specific numbers in your reasoning. Keep total response under 75 words with actionable, location-appropriate design advice.",
                            },
                            {
                              role: "user",
                              content: `For this specific ${
                                climateInfo.mainClass
                              }${climateInfo.subClass} (${
                                climateInfo.fullName
                              }) climate location, provide targeted building design recommendations based on these thermal performance metrics:

LOCATION CLIMATE PROFILE:
- Köppen Classification: ${climateInfo.mainClass}${climateInfo.subClass}
- Heating Degree Days: ${hdd.toFixed(0)} (base 18°C)
- Cooling Degree Days: ${cdd.toFixed(0)} (base 18°C)
- Average Daily Temperature Swing: ${avgDTR.toFixed(1)}°C
- Extreme Range: ${annualMin.toFixed(1)}°C to ${annualMax.toFixed(1)}°C
- Annual Average: ${annualAvg.toFixed(1)}°C

Based on this SPECIFIC climate classification and thermal data, provide design recommendations with clear reasoning:

1. THERMAL ENVELOPE: Explain insulation strategy and R-values based on the specific HDD (${hdd.toFixed(
                                0
                              )}) and CDD (${cdd.toFixed(
                                0
                              )}) ratio. Reference these numbers in your reasoning.

2. CONSTRUCTION APPROACH: Justify wall construction and thermal mass approach based on the ${avgDTR.toFixed(
                                1
                              )}°C daily temperature swing and ${(
                                annualMax - annualMin
                              ).toFixed(
                                1
                              )}°C annual range. Use these specific values in your explanation.

3. PASSIVE DESIGN: Recommend window/glazing and orientation strategies based on the heating demand of ${hdd.toFixed(
                                0
                              )} HDD versus cooling demand of ${cdd.toFixed(
                                0
                              )} CDD. Explain why these specific numbers drive your recommendations.

Use the actual thermal values (${hdd.toFixed(0)} HDD, ${cdd.toFixed(
                                0
                              )} CDD, ${avgDTR.toFixed(
                                1
                              )}°C DTR) to justify each design decision. Be specific about how these numbers inform your recommendations.`,
                            },
                          ],
                        }),
                      }
                    );

                    if (!response.ok) {
                      if (response.status === 503) {
                        throw new Error("AI service temporarily unavailable");
                      } else if (response.status === 401) {
                        throw new Error("Authentication failed");
                      } else if (response.status === 429) {
                        throw new Error("Rate limit exceeded");
                      }
                      throw new Error(`Request failed: ${response.status}`);
                    }

                    const data = await response.json();

                    if (
                      data.choices &&
                      data.choices[0] &&
                      data.choices[0].message
                    ) {
                      setDesignSuggestions(data.choices[0].message.content);
                    } else if (data.error) {
                      throw new Error(data.error.message || "Unknown AI error");
                    }
                  } catch (error) {
                    console.error("AI design suggestions error:", error);
                    setDesignError(error.message);
                  } finally {
                    setLoadingDesignSuggestions(false);
                  }
                }}
                disabled={loadingDesignSuggestions}
              >
                {loadingDesignSuggestions ? "..." : "ClimaPro.AI"}
              </button>
            </div>
          </div>

          <div className="flex items-end justify-between mt-auto">
            <div>
              <div className="text-sm text-gray-600 mb-1">+ HEATING</div>
              <div className="text-sm text-gray-600">+ COOLING</div>
            </div>
            <div className="text-right">
              <div className="text-5xl font-bold text-gray-900">
                {temperatureData.data
                  ? (
                      (temperatureData.data.filter(
                        (d) => d.mean >= 18 && d.mean <= 26
                      ).length /
                        temperatureData.data.length) *
                      100
                    ).toFixed(1)
                  : "--"}
                %
              </div>
              <div className="text-sm text-gray-500 font-bold">
                Comfortable Days
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Landscape Graph Card - Bottom Section */}
      <div className="bg-gray-100 border border-gray-500 rounded-[2rem] overflow-hidden">
        <button
          className="w-full p-4 flex justify-between items-center text-left hover:bg-gray-200 transition-colors"
          onClick={() => {
            const graphContent = document.getElementById("graph-content");
            const chevron = document.getElementById("graph-chevron");
            if (
              graphContent.style.display === "none" ||
              !graphContent.style.display
            ) {
              graphContent.style.display = "block";
              chevron.style.transform = "rotate(180deg)";
            } else {
              graphContent.style.display = "none";
              chevron.style.transform = "rotate(0deg)";
            }
          }}
        >
          <div className="flex items-center space-x-4 ml-4">
            <h3 className="text-lg font-bold text-gray-800 tracking-wide">
              GRAPH DATA
            </h3>
            <button
              className="bg-gray-600 hover:bg-[#206ab2] text-white px-1.5 py-1 rounded text-xs font-bold transition-colors"
              onClick={async (e) => {
                e.stopPropagation(); // Prevent triggering the expand/collapse

                if (!OPENROUTER_API_KEY) {
                  setAiError("OpenRouter API key not configured");
                  return;
                }

                // Clear previous results before starting new analysis
                setAiInterpretation(null);
                setAiError(null);
                setLoadingAiInterpretation(true);

                try {
                  // Calculate temperature statistics
                  const tempStats = {
                    annualAvg: temperatureData.data
                      ? (
                          temperatureData.data.reduce(
                            (sum, d) => sum + d.mean,
                            0
                          ) / temperatureData.data.length
                        ).toFixed(1)
                      : 0,
                    maxTemp: temperatureData.data
                      ? Math.max(
                          ...temperatureData.data.map((d) => d.max)
                        ).toFixed(1)
                      : 0,
                    minTemp: temperatureData.data
                      ? Math.min(
                          ...temperatureData.data.map((d) => d.min)
                        ).toFixed(1)
                      : 0,
                    tempRange: temperatureData.data
                      ? (
                          Math.max(...temperatureData.data.map((d) => d.max)) -
                          Math.min(...temperatureData.data.map((d) => d.min))
                        ).toFixed(1)
                      : 0,
                    // Calculate seasonal averages from daily data
                    winterAvg: temperatureData.data
                      ? (() => {
                          const winterData = temperatureData.data.filter(
                            (item, index) => {
                              const month = item.month
                                ? item.month
                                : Math.floor(
                                    index / (temperatureData.data.length / 12)
                                  ) + 1;
                              return month === 12 || month === 1 || month === 2;
                            }
                          );
                          return winterData.length > 0
                            ? (
                                winterData.reduce((sum, d) => sum + d.mean, 0) /
                                winterData.length
                              ).toFixed(1)
                            : 0;
                        })()
                      : 0,
                    springAvg: temperatureData.data
                      ? (() => {
                          const springData = temperatureData.data.filter(
                            (item, index) => {
                              const month = item.month
                                ? item.month
                                : Math.floor(
                                    index / (temperatureData.data.length / 12)
                                  ) + 1;
                              return month === 3 || month === 4 || month === 5;
                            }
                          );
                          return springData.length > 0
                            ? (
                                springData.reduce((sum, d) => sum + d.mean, 0) /
                                springData.length
                              ).toFixed(1)
                            : 0;
                        })()
                      : 0,
                    summerAvg: temperatureData.data
                      ? (() => {
                          const summerData = temperatureData.data.filter(
                            (item, index) => {
                              const month = item.month
                                ? item.month
                                : Math.floor(
                                    index / (temperatureData.data.length / 12)
                                  ) + 1;
                              return month === 6 || month === 7 || month === 8;
                            }
                          );
                          return summerData.length > 0
                            ? (
                                summerData.reduce((sum, d) => sum + d.mean, 0) /
                                summerData.length
                              ).toFixed(1)
                            : 0;
                        })()
                      : 0,
                    fallAvg: temperatureData.data
                      ? (() => {
                          const fallData = temperatureData.data.filter(
                            (item, index) => {
                              const month = item.month
                                ? item.month
                                : Math.floor(
                                    index / (temperatureData.data.length / 12)
                                  ) + 1;
                              return (
                                month === 9 || month === 10 || month === 11
                              );
                            }
                          );
                          return fallData.length > 0
                            ? (
                                fallData.reduce((sum, d) => sum + d.mean, 0) /
                                fallData.length
                              ).toFixed(1)
                            : 0;
                        })()
                      : 0,
                  };

                  const response = await fetch(
                    "https://openrouter.ai/api/v1/chat/completions",
                    {
                      method: "POST",
                      headers: {
                        Authorization: `Bearer ${OPENROUTER_API_KEY}`,
                        "HTTP-Referer": window.location.origin,
                        "X-Title": "ClimaPro.AI",
                        "Content-Type": "application/json",
                      },
                      body: JSON.stringify({
                        model: "anthropic/claude-3.7-sonnet",
                        route: "fallback",
                        messages: [
                          {
                            role: "system",
                            content:
                              "You are an expert climate data analyst specializing in temperature graph interpretation. Focus exclusively on analyzing temperature graph patterns, trends, seasonal variations, and ranges. Provide insights specifically about what the temperature graph reveals, not general climate advice. Keep responses concise, 100 words one paragraph, focusing on graph-specific observations.",
                          },
                          {
                            role: "user",
                            content: `Analyze the temperature graph patterns for this ${
                              climateInfo.mainClass
                            }${climateInfo.subClass} (${
                              climateInfo.fullName
                            }) climate location:

ANNUAL STATISTICS:
- Annual Average: ${tempStats.annualAvg}°C
- Absolute Maximum: ${tempStats.maxTemp}°C
- Absolute Minimum: ${tempStats.minTemp}°C
- Total Annual Range: ${tempStats.tempRange}°C

SEASONAL PATTERNS:
- Winter (Dec-Feb): ${tempStats.winterAvg}°C average
- Spring (Mar-May): ${tempStats.springAvg}°C average  
- Summer (Jun-Aug): ${tempStats.summerAvg}°C average
- Fall (Sep-Nov): ${tempStats.fallAvg}°C average

SEASONAL TEMPERATURE RANGES:
- Winter-Summer difference: ${(
                              parseFloat(tempStats.summerAvg) -
                              parseFloat(tempStats.winterAvg)
                            ).toFixed(1)}°C
- Spring-Fall difference: ${(
                              parseFloat(tempStats.springAvg) -
                              parseFloat(tempStats.fallAvg)
                            ).toFixed(1)}°C

Analyze specifically what this temperature graph reveals about:
1. Seasonal transition patterns and smoothness
2. Temperature range and variability within this ${climateInfo.mainClass}${
                              climateInfo.subClass
                            } classification
3. Peak timing and duration patterns visible in the graph
4. Graph trend characteristics that define this climate type
5. Notable temperature extremes or patterns shown in the data

Focus only on what the temperature graph data and patterns show, not design recommendations.`,
                          },
                        ],
                      }),
                    }
                  );

                  if (!response.ok) {
                    if (response.status === 503) {
                      throw new Error("AI service temporarily unavailable");
                    } else if (response.status === 401) {
                      throw new Error("Authentication failed");
                    } else if (response.status === 429) {
                      throw new Error("Rate limit exceeded");
                    }
                    throw new Error(`Request failed: ${response.status}`);
                  }

                  const data = await response.json();

                  if (
                    data.choices &&
                    data.choices[0] &&
                    data.choices[0].message
                  ) {
                    setAiInterpretation(data.choices[0].message.content);
                  } else if (data.error) {
                    throw new Error(data.error.message || "Unknown AI error");
                  }
                } catch (error) {
                  console.error("AI interpretation error:", error);
                  setAiError(error.message);
                } finally {
                  setLoadingAiInterpretation(false);
                }
              }}
              disabled={loadingAiInterpretation}
            >
              {loadingAiInterpretation ? "..." : "ClimaPro.AI"}
            </button>
          </div>
          <svg
            id="graph-chevron"
            className="w-8 h-8 text-gray-600 transition-transform duration-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        <div
          id="graph-content"
          style={{ display: "none" }}
          className="px-8 pb-8"
        >
          {temperatureData && temperatureData.data && (
            <>
              {/* Conditional Layout: Split when AI content exists, full width otherwise */}
              {aiInterpretation || aiError || loadingAiInterpretation ? (
                <div className="flex gap-6">
                  {/* AI Interpretation Section - Left 30% */}
                  <div className="w-[30%] bg-gray-100">
                    <div className="h-full">
                      <h4 className="font-bold text-gray-800 mb-4">
                        AI Temperature Analysis
                      </h4>
                      {loadingAiInterpretation && (
                        <div className="space-y-3">
                          {/* Skeleton loading rectangles with color animation */}
                          <style jsx>{`
                            @keyframes colorWave1 {
                              0%,
                              100% {
                                background-color: #d1d5db;
                              }
                              50% {
                                background-color: #9ca3af;
                              }
                            }
                            @keyframes colorWave2 {
                              0%,
                              100% {
                                background-color: #9ca3af;
                              }
                              50% {
                                background-color: #d1d5db;
                              }
                            }
                            @keyframes colorWave3 {
                              0%,
                              100% {
                                background-color: #e5e7eb;
                              }
                              50% {
                                background-color: #9ca3af;
                              }
                            }
                            .animate-wave-1 {
                              animation: colorWave1 2s ease-in-out infinite;
                            }
                            .animate-wave-2 {
                              animation: colorWave2 2s ease-in-out infinite 0.3s;
                            }
                            .animate-wave-3 {
                              animation: colorWave3 2s ease-in-out infinite 0.6s;
                            }
                          `}</style>
                          <div className="h-3 rounded animate-wave-1"></div>
                          <div className="h-3 rounded animate-wave-2 w-4/5"></div>
                          <div className="h-3 rounded animate-wave-3 w-3/5"></div>
                          <div className="h-3 rounded animate-wave-1 w-5/6"></div>
                          <div className="h-3 rounded animate-wave-2 w-2/3"></div>
                          <div className="h-3 rounded animate-wave-3"></div>
                          <div className="h-3 rounded animate-wave-1 w-3/4"></div>
                          <div className="h-3 rounded animate-wave-2 w-4/5"></div>
                          <div className="h-3 rounded animate-wave-3 w-1/2"></div>
                          <div className="h-3 rounded animate-wave-1 w-3/5"></div>
                          <div className="h-3 rounded animate-wave-2 w-5/6"></div>
                          <div className="h-3 rounded animate-wave-3 w-2/3"></div>
                        </div>
                      )}
                      {aiError && (
                        <div className="text-red-600 text-sm font-bold">
                          Error: {aiError}
                        </div>
                      )}
                      {aiInterpretation && (
                        <div className="text-gray-600 text-sm leading-relaxed whitespace-pre-wrap font-bold">
                          {aiInterpretation}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Graph Section - Right 70% when AI content exists */}
                  <div className="w-[70%] h-96 bg-gray-100 rounded-2xl p-6 flex flex-col justify-center">
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={temperatureData.data.map((item, index) => ({
                            month: item.month
                              ? [
                                  "Jan",
                                  "Feb",
                                  "Mar",
                                  "Apr",
                                  "May",
                                  "Jun",
                                  "Jul",
                                  "Aug",
                                  "Sep",
                                  "Oct",
                                  "Nov",
                                  "Dec",
                                ][item.month - 1]
                              : [
                                  "Jan",
                                  "Feb",
                                  "Mar",
                                  "Apr",
                                  "May",
                                  "Jun",
                                  "Jul",
                                  "Aug",
                                  "Sep",
                                  "Oct",
                                  "Nov",
                                  "Dec",
                                ][
                                  Math.floor(
                                    index / (temperatureData.data.length / 12)
                                  )
                                ],
                            max: item.max,
                            min: item.min,
                            mean: item.mean,
                            dayOfYear: item.day_of_year || index,
                            originalIndex: index,
                          }))}
                          margin={{
                            top: 20,
                            right: 30,
                            left: 20,
                            bottom: 20,
                          }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#f0f0f0"
                          />
                          <XAxis
                            dataKey="month"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 12, fill: "#666" }}
                            interval={
                              Math.floor(temperatureData.data.length / 12) - 1
                            }
                          />
                          <YAxis
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 12, fill: "#666" }}
                            label={{
                              value: "°C",
                              angle: -90,
                              position: "insideLeft",
                            }}
                            domain={[
                              (dataMin) => Math.floor(dataMin - 5),
                              (dataMax) => Math.ceil(dataMax + 5),
                            ]}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: "white",
                              border: "1px solid #ccc",
                              borderRadius: "8px",
                            }}
                            formatter={(value, name) => [
                              `${value.toFixed(1)}°C`,
                              name === "max"
                                ? "Maximum"
                                : name === "min"
                                ? "Minimum"
                                : "Average",
                            ]}
                          />

                          <ReferenceLine
                            y={0}
                            stroke="#e0e0e0"
                            strokeDasharray="2 2"
                          />

                          <Line
                            type="monotone"
                            dataKey="max"
                            stroke="#dc2626"
                            strokeWidth={1}
                            dot={false}
                            name="max"
                          />
                          <Line
                            type="monotone"
                            dataKey="min"
                            stroke="#2563eb"
                            strokeWidth={1}
                            dot={false}
                            name="min"
                          />
                          <Line
                            type="monotone"
                            dataKey="mean"
                            stroke="#d97706"
                            strokeWidth={1}
                            strokeDasharray="3 2"
                            dot={false}
                            name="mean"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>

                    <div className="flex justify-center space-x-8 mt-4 text-sm">
                      <div className="flex items-center">
                        <div className="w-6 h-0.5 bg-red-600 mr-3"></div>
                        <span className="text-gray-700">
                          Maximum Temperature
                        </span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-6 h-0.5 bg-blue-600 mr-3"></div>
                        <span className="text-gray-700">
                          Minimum Temperature
                        </span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-6 h-0.5 bg-amber-600 mr-3 border-dashed border-t"></div>
                        <span className="text-gray-700">
                          Average Temperature
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* Full Width Graph - When no AI content */
                <div className="h-96 bg-gray-100 rounded-2xl p-6 flex flex-col justify-center">
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={temperatureData.data.map((item, index) => ({
                          month: item.month
                            ? [
                                "Jan",
                                "Feb",
                                "Mar",
                                "Apr",
                                "May",
                                "Jun",
                                "Jul",
                                "Aug",
                                "Sep",
                                "Oct",
                                "Nov",
                                "Dec",
                              ][item.month - 1]
                            : [
                                "Jan",
                                "Feb",
                                "Mar",
                                "Apr",
                                "May",
                                "Jun",
                                "Jul",
                                "Aug",
                                "Sep",
                                "Oct",
                                "Nov",
                                "Dec",
                              ][
                                Math.floor(
                                  index / (temperatureData.data.length / 12)
                                )
                              ],
                          max: item.max,
                          min: item.min,
                          mean: item.mean,
                          dayOfYear: item.day_of_year || index,
                          originalIndex: index,
                        }))}
                        margin={{
                          top: 20,
                          right: 30,
                          left: 20,
                          bottom: 20,
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                          dataKey="month"
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 12, fill: "#666" }}
                          interval={
                            Math.floor(temperatureData.data.length / 12) - 1
                          }
                        />
                        <YAxis
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 12, fill: "#666" }}
                          label={{
                            value: "°C",
                            angle: -90,
                            position: "insideLeft",
                          }}
                          domain={[
                            (dataMin) => Math.floor(dataMin - 5),
                            (dataMax) => Math.ceil(dataMax + 5),
                          ]}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "white",
                            border: "1px solid #ccc",
                            borderRadius: "8px",
                          }}
                          formatter={(value, name) => [
                            `${value.toFixed(1)}°C`,
                            name === "max"
                              ? "Maximum"
                              : name === "min"
                              ? "Minimum"
                              : "Average",
                          ]}
                        />

                        <ReferenceLine
                          y={0}
                          stroke="#e0e0e0"
                          strokeDasharray="2 2"
                        />

                        <Line
                          type="monotone"
                          dataKey="max"
                          stroke="#dc2626"
                          strokeWidth={1}
                          dot={false}
                          name="max"
                        />
                        <Line
                          type="monotone"
                          dataKey="min"
                          stroke="#2563eb"
                          strokeWidth={1}
                          dot={false}
                          name="min"
                        />
                        <Line
                          type="monotone"
                          dataKey="mean"
                          stroke="#d97706"
                          strokeWidth={1}
                          strokeDasharray="3 2"
                          dot={false}
                          name="mean"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="flex justify-center space-x-8 mt-4 text-sm">
                    <div className="flex items-center">
                      <div className="w-6 h-0.5 bg-red-600 mr-3"></div>
                      <span className="text-gray-700">Maximum Temperature</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-6 h-0.5 bg-blue-600 mr-3"></div>
                      <span className="text-gray-700">Minimum Temperature</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-6 h-0.5 bg-amber-600 mr-3 border-dashed border-t"></div>
                      <span className="text-gray-700">Average Temperature</span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemperatureAnalysis;
